import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import CategoryIcon from "@/components/CategoryIcon";
import { CATEGORY_LINKS, PRODUCTS } from "@/data/products";

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(false);
  const [catOpen, setCatOpen] = useState(true);
  const { scrollY } = useScroll();
  const { pathname } = useLocation();

  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const onDarkHero = pathname === "/" && !scrolled && !open;
  const linkBase = "font-display text-[15px] font-medium tracking-tight transition-colors duration-300";
  const linkTone = "text-ink/65 hover:text-ink";
  const collectionsActive = CATEGORY_LINKS.some((c) => pathname === c.to) || pathname === "/collections";

  const count = (label) => PRODUCTS.filter((p) => p.category === label).length;

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled || onDarkHero
            ? "bg-white/70 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            : "bg-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-6 py-4 md:px-10">
          <Link to="/" data-testid="nav-logo" onClick={() => setOpen(false)} className="relative z-10 block shrink-0">
            <img
              src="/logo-black.webp"
              alt="SanCub"
              className="h-[52px] w-auto transition-[filter] duration-500 md:h-14"
            />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
            <div
              className="relative"
              onMouseEnter={() => setDrop(true)}
              onMouseLeave={() => setDrop(false)}
            >
              <button
                type="button"
                data-testid="nav-collections-trigger"
                aria-expanded={drop}
                className={`flex items-center gap-1.5 py-2 ${linkBase} ${
                  collectionsActive ? "text-ink" : linkTone
                }`}
              >
                Collections
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${drop ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {drop && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="absolute left-1/2 top-full w-[560px] -translate-x-1/2 pt-4"
                    data-testid="collections-dropdown"
                  >
                    <div className="rounded-[1.5rem] bg-white p-6 shadow-[0_24px_60px_rgb(0,0,0,0.12)]">
                      <div className="grid grid-cols-3 gap-1.5">
                        {CATEGORY_LINKS.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            data-testid={`dropdown-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                              `group flex items-center gap-3.5 rounded-2xl px-4 py-3 transition-colors duration-200 ${
                                isActive ? "bg-sand" : "hover:bg-sand/70"
                              }`
                            }
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sand text-ink/70 transition-colors duration-200 group-hover:bg-white group-hover:text-ink">
                              <CategoryIcon category={c.label} className="h-5 w-5" />
                            </span>
                            <span>
                              <span className="block font-display text-[15px] font-medium tracking-tight text-ink">
                                {c.label}
                              </span>
                              <span className="mt-0.5 block text-[11px] font-medium text-ink/40">
                                {count(c.label)} products
                              </span>
                            </span>
                          </NavLink>
                        ))}
                      </div>
                      <Link
                        to="/collections"
                        data-testid="dropdown-view-all"
                        className="group mt-4 flex items-center justify-between rounded-2xl bg-ink px-5 py-3.5 text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-ink/90"
                      >
                        View the full range
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavLink
              to="/stockists"
              data-testid="nav-link-stockists"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-ink" : linkTone}`
              }
            >
              Stockists
            </NavLink>
            <NavLink
              to="/about"
              data-testid="nav-link-about"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-ink" : linkTone}`
              }
            >
              About
            </NavLink>
            <NavLink
              to="/contact"
              data-testid="nav-link-contact"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-ink" : linkTone}`
              }
            >
              Contact
            </NavLink>
            <Link
              to="/contact"
              data-testid="nav-enquire-btn"
              className="group flex shrink-0 items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.03] hover:bg-ink/90"
            >
              Enquire
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            className="text-ink lg:hidden"
            data-testid="nav-menu-toggle"
            onClick={() => { setOpen(!open); if (open) setCatOpen(true); }}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            key={pathname + "-menu"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-sand px-8 pb-10 pt-28 lg:hidden"
            data-testid="mobile-menu"
          >
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.45, ease: "easeOut" }}
            >
              <NavLink
                to="/"
                data-testid="mobile-nav-home"
                onClick={() => setOpen(false)}
                className="block py-3 font-display text-3xl font-medium tracking-tight text-ink"
              >
                Home
              </NavLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.09, duration: 0.45, ease: "easeOut" }}
            >
              <button
                type="button"
                onClick={() => setCatOpen(!catOpen)}
                aria-expanded={catOpen}
                data-testid="mobile-nav-collections-toggle"
                className="flex w-full items-center justify-between py-3 font-display text-3xl font-medium tracking-tight text-ink"
              >
                Collections
                <ChevronDown className={`h-6 w-6 transition-transform duration-300 ${catOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {catOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="overflow-hidden"
                    data-testid="mobile-collections-panel"
                  >
                    <div className="grid grid-cols-2 gap-2 pb-2.5 pt-1">
                      {CATEGORY_LINKS.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-tile-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sand text-ink/70">
                            <CategoryIcon category={c.label} className="h-5 w-5" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-[13px] font-semibold tracking-tight text-ink">{c.label}</span>
                            <span className="mt-0.5 block text-[10px] font-medium text-ink/40">
                              {count(c.label)} products
                            </span>
                          </span>
                        </NavLink>
                      ))}
                    </div>
                    <NavLink
                      to="/collections"
                      onClick={() => setOpen(false)}
                      data-testid="mobile-nav-view-all"
                      className="mb-1 flex items-center justify-between rounded-2xl bg-ink px-5 py-3.5 text-[13px] font-semibold text-white"
                    >
                      View the full range
                      <ArrowUpRight className="h-4 w-4" />
                    </NavLink>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {[
              { to: "/stockists", label: "Stockists", id: "mobile-nav-stockists" },
              { to: "/about", label: "About", id: "mobile-nav-about" },
              { to: "/contact", label: "Contact", id: "mobile-nav-contact" },
            ].map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.13 + i * 0.04, duration: 0.45, ease: "easeOut" }}
              >
                <NavLink
                  to={l.to}
                  data-testid={l.id}
                  onClick={() => setOpen(false)}
                  className="block py-3 font-display text-3xl font-medium tracking-tight text-ink"
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.45, ease: "easeOut" }}
              className="mt-6"
            >
              <Link
                to="/contact"
                data-testid="mobile-nav-enquire"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white"
              >
                Enquire
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
